import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useRef, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../api/client'
import {
  createFloor,
  deleteFloor,
  drawFloorCanvas,
  floorListQuery,
  floorPageQuery,
  updateFloor,
  uploadFloorImage,
  type FloorList,
  type FloorPage,
  type FloorSummary,
} from '../api/floorPlans'
import { AccessPointsPanel } from '../settings/agent_install/AccessPointsPanel'
import { isForbidden, Forbidden } from '../ui/Forbidden'
import { LoadFailed } from '../ui/LoadFailed'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { FloorPlan } from './FloorPlan'
import { CANVAS_DEFAULT_M, CANVAS_MAX_M, CANVAS_MIN_M, canvasSide } from './grid'
import './FloorPlan.css'

/**
 * Settings -> Floor plans (`/app/settings/floor_plans`) - Connection
 * Quality, faza 5. Legata din panoul Connection Quality de la Install
 * Options, unde stau deja setarile CQ si lista AP-urilor firmei.
 *
 * Etajele firmei, grupate pe sediu: creare, redenumire, stergere (cu tot ce
 * e pe ele). Un etaj se deschide la `/app/settings/floor_plans/<id>`.
 * Citirea cu `settings.my_company.read`, scrierea cu `.edit`.
 */
export function FloorPlansPage() {
  const { data, error, refetch } = useQuery(floorListQuery)
  if (error) return isForbidden(error) ? <Forbidden /> : <LoadFailed error={error} onRetry={() => void refetch()} what="Floor plans" />
  if (!data) return <Spinner />
  return <FloorList page={data} />
}

function FloorList({ page }: { page: FloorList }) {
  const query = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<number | null>(null)
  const byOffice = new Map<number | null, FloorSummary[]>()
  for (const f of page.floors) byOffice.set(f.office_id, [...(byOffice.get(f.office_id) ?? []), f])
  const officeName = (id: number | null) => (id === null ? 'No office' : (page.offices.find((o) => o.id === id)?.name ?? `Office ${id}`))
  const groups = [...byOffice.entries()].sort((a, b) => officeName(a[0]).localeCompare(officeName(b[0])))

  const remove = (f: FloorSummary) => {
    if (!window.confirm(`Delete "${f.name}"? Its plan, everything placed on it and all its surveys are deleted too.`)) return
    deleteFloor(f.id)
      .then((next) => {
        query.setQueryData(floorListQuery.queryKey, next)
        toast('Deleted.', 'Floor plans')
      })
      .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'It could not be deleted.', 'Floor plans', 'error'))
  }

  return (
    <div id="content" className="fp-page" data-page="floor-plans">
      {/* Acelasi titlu de sectiune ca "Wi-Fi access points" de dedesubt
          (`.od-cq-section`): titlul, apoi fraza SUB el (30 septembrie 2026). */}
      <div className="fp-page-head fp-page-head-section">
        <div className="od-cq-section fp-grow">
          <h3>Floor plans</h3>
          <p>Where the access points and devices are, and how good the Wi-Fi is on each floor.</p>
        </div>
        {page.canWrite && !adding ? (
          <button type="button" className="btn btn-success" onClick={() => setAdding(true)}>
            <i className="fas fa-plus" aria-hidden="true"></i> New floor
          </button>
        ) : null}
      </div>

      {adding ? (
        <FloorForm
          page={page}
          onCancel={() => setAdding(false)}
          onSubmit={(fields) =>
            createFloor(fields).then((next) => {
              query.setQueryData(floorListQuery.queryKey, next)
              setAdding(false)
              toast('Floor added. Open it to upload the plan.', 'Floor plans')
            })
          }
        />
      ) : null}

      {page.floors.length === 0 ? (
        <p className="fp-muted">No floor plans yet.{page.canWrite ? ' Add a floor, upload its plan (PNG or JPG) and calibrate it.' : ''}</p>
      ) : (
        <>
        <p className="fp-muted fp-floors-hint">Click a floor to open its plan.</p>
        {groups.map(([officeId, floors]) => (
          <div key={String(officeId)} data-office={officeId ?? 'none'}>
            <h4 className="fp-office">{officeName(officeId)}</h4>
            <table className="table table-hover fp-floors">
              <thead>
                <tr>
                  <th>Floor</th>
                  <th>Level</th>
                  <th>Plan</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {floors.map((f) =>
                  editing === f.id ? (
                    <tr key={f.id}>
                      <td colSpan={5}>
                        <FloorForm
                          page={page}
                          floor={f}
                          onCancel={() => setEditing(null)}
                          onSubmit={(fields) =>
                            updateFloor(f.id, fields).then((next) => {
                              query.setQueryData(floorListQuery.queryKey, next)
                              void query.invalidateQueries({ queryKey: ['settings', 'floor-plans', f.id] })
                              setEditing(null)
                            })
                          }
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={f.id} data-floor={f.id} className="fp-floor-row" onClick={openFloorRow}>
                      <td>
                        <Link to="/settings/floor_plans/$id" params={{ id: String(f.id) }} className="fp-floor-link" title="Open floor plan" data-floor-link>
                          <i className="fas fa-map-marked-alt fp-floor-icon" aria-hidden="true"></i>
                          <strong>{f.name}</strong>
                          <i className="fas fa-chevron-right fp-floor-arrow" aria-hidden="true"></i>
                        </Link>
                      </td>
                      <td>{f.level_number ?? '-'}</td>
                      <td>
                        {f.canvas && !f.has_image ? (
                          <span data-plan="drawn">drawn (no plan)</span>
                        ) : !f.has_image ? (
                          <span className="fp-muted">not uploaded</span>
                        ) : f.scale_px_per_m === null ? (
                          <span className="text-warning">not calibrated</span>
                        ) : (
                          'ready'
                        )}
                      </td>
                      <td className="fp-muted">{f.updated_at ?? '-'}</td>
                      <td className="fp-actions">
                        {page.canWrite && !f.has_image && !f.canvas ? (
                          <Link to="/settings/floor_plans/$id" params={{ id: String(f.id) }} className="btn btn-primary btn-xs" data-action="upload-or-draw">
                            <i className="fas fa-pencil-ruler" aria-hidden="true"></i> Upload or draw
                          </Link>
                        ) : null}
                        {page.canWrite ? (
                          <>
                            <button type="button" className="btn btn-link btn-xs" onClick={() => setEditing(f.id)} aria-label={`Rename ${f.name}`}>
                              <i className="fas fa-pen" aria-hidden="true"></i> Edit
                            </button>
                            <button type="button" className="btn btn-link btn-xs text-danger" onClick={() => remove(f)} aria-label={`Delete ${f.name}`}>
                              <i className="fas fa-trash" aria-hidden="true"></i> Delete
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        ))}
        </>
      )}

      {/* Acelasi panou ca pe Settings -> Connection Quality (30 sep 2026):
          AP-urile se pun pe etaje, deci omul le vrea langa lista etajelor.
          Panoul isi face singur verificarea de drept - fara ea nu apare. */}
      <div className="fp-aps">
        <AccessPointsPanel />
      </div>
    </div>
  )
}

/**
 * Tot randul etajului deschide planul, nu doar numele (30 sep 2026). Un clic
 * pe Edit/Delete sau pe un camp isi face treaba lui; altfel se apasa linkul
 * numelui, deci navigarea ramane a routerului.
 */
function openFloorRow(e: MouseEvent<HTMLElement>) {
  const target = e.target as HTMLElement
  if (target.closest('a, button, input, select, textarea, label, form')) return
  e.currentTarget.querySelector<HTMLAnchorElement>('a[data-floor-link]')?.click()
}

function FloorForm({
  page,
  floor,
  onSubmit,
  onCancel,
}: {
  page: FloorList
  floor?: FloorSummary
  onSubmit(fields: { name: string; office_id: number | null; level_number: number | null }): Promise<void>
  onCancel(): void
}) {
  const [name, setName] = useState(floor?.name ?? '')
  const [office, setOffice] = useState(floor?.office_id != null ? String(floor.office_id) : '')
  const [level, setLevel] = useState(floor?.level_number != null ? String(floor.level_number) : '')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setProblem('')
    onSubmit({ name: name.trim(), office_id: office === '' ? null : Number(office), level_number: level.trim() === '' ? null : Number(level) }).catch((err: unknown) => {
      setProblem(err instanceof ApiError && err.message ? err.message : 'It could not be saved.')
      setBusy(false)
    })
  }

  return (
    <form className="fp-floor-form" onSubmit={submit}>
      <input className="form-control" aria-label="Floor name" placeholder="Floor name, e.g. Ground floor" maxLength={128} value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
      <select className="form-control" aria-label="Office" value={office} disabled={busy} onChange={(e) => setOffice(e.target.value)}>
        <option value="">No office</option>
        {page.offices.map((o) => (
          <option key={o.id} value={String(o.id)}>
            {o.name}
          </option>
        ))}
      </select>
      <input className="form-control" aria-label="Level" placeholder="Level (0, 1, -1...)" inputMode="numeric" value={level} disabled={busy} onChange={(e) => setLevel(e.target.value)} />
      <button type="submit" className="btn btn-success" disabled={busy || name.trim() === ''}>
        Save
      </button>
      <button type="button" className="btn btn-default" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
      {problem ? (
        <span className="text-danger" role="alert">
          {problem}
        </span>
      ) : null}
    </form>
  )
}

/** Un etaj: `/app/settings/floor_plans/<id>`. */
export function FloorPlanPage({ id }: { id: number }) {
  const { data, error, refetch } = useQuery(floorPageQuery(id))
  if (error) {
    if (isForbidden(error)) return <Forbidden />
    if (error instanceof ApiError && error.status === 404) {
      return (
        <div id="content" className="fp-page">
          <p>This floor does not exist in the selected company.</p>
          <Link to="/settings/floor_plans">Back to floor plans</Link>
        </div>
      )
    }
    return <LoadFailed error={error} onRetry={() => void refetch()} what="The floor plan" />
  }
  if (!data) return <Spinner />
  return <FloorScreen page={data} />
}

function FloorScreen({ page }: { page: FloorPage }) {
  const query = useQueryClient()
  /* Desenat fara plan: fara imagine, cu marime si scara. */
  const canvas = page.floor.canvas === true && !page.floor.has_image
  const [mode, setMode] = useState<'view' | 'edit'>(page.canWrite && ((!page.floor.has_image && !canvas) || page.floor.scale_px_per_m === null) ? 'edit' : 'view')
  const [uploading, setUploading] = useState(false)
  const [sizing, setSizing] = useState(false)
  const file = useRef<HTMLInputElement | null>(null)
  const office = page.offices.find((o) => o.id === page.floor.office_id)?.name

  const upload = (f: File | undefined) => {
    if (!f) return
    if (!/\.(png|jpe?g)$/i.test(f.name)) {
      toast('Export the plan as PNG or JPG.', 'Floor plan', 'error')
      return
    }
    setUploading(true)
    uploadFloorImage(page.floor.id, f)
      .then((next) => {
        query.setQueryData(floorPageQuery(page.floor.id).queryKey, next)
        void query.invalidateQueries({ queryKey: floorListQuery.queryKey })
        toast(next.floor.scale_px_per_m === null ? 'Plan uploaded. Now calibrate it.' : 'Plan uploaded.', 'Floor plan')
        setSizing(false)
        setMode('edit')
      })
      .catch((e: unknown) => toast(e instanceof ApiError && e.message ? e.message : 'The plan could not be uploaded.', 'Floor plan', 'error'))
      .finally(() => {
        setUploading(false)
        if (file.current) file.current.value = ''
      })
  }

  return (
    <div id="content" className="fp-page" data-page="floor-plan">
      <div className="fp-page-head">
        <Link to="/settings/floor_plans" className="btn btn-default btn-sm" aria-label="All floor plans">
          <i className="fas fa-arrow-left" aria-hidden="true"></i>
        </Link>
        <h3>
          {page.floor.name}
          {office ? <small> {office}</small> : null}
        </h3>
      </div>
      {!page.floor.has_image && !canvas && page.canWrite ? (
        <p className="fp-muted">
          PNG or JPG, up to 20 MB. PDF or DWG: export the plan as PNG or JPG first. Large plans are reduced to 4096 px on the long side. No plan at hand? Draw the floor
          without one: a white sheet with a 1 m grid, where you draw the walls and place the access points and devices.
        </p>
      ) : null}
      {canvas && page.canWrite ? (
        <p className="fp-muted" data-hint="canvas">
          Drawn without a plan{canvasSize(page) ? `: ${canvasSize(page)}` : ''}. You can upload a plan at any time - it replaces the white sheet, the walls and
          everything placed stay, and you calibrate it.
        </p>
      ) : null}
      {sizing && page.canWrite && !page.floor.has_image ? (
        <CanvasForm
          page={page}
          onCancel={() => setSizing(false)}
          onDone={(next) => {
            query.setQueryData(floorPageQuery(page.floor.id).queryKey, next)
            void query.invalidateQueries({ queryKey: floorListQuery.queryKey })
            toast(canvas ? 'Canvas resized.' : 'Floor ready to draw.', 'Floor plan')
            setSizing(false)
            setMode('edit')
          }}
        />
      ) : null}
      <FloorPlan
        key={`${page.floor.id}:${page.floor.image_url ?? ''}:${page.floor.image_w_px ?? 0}x${page.floor.image_h_px ?? 0}`}
        page={page}
        mode={mode}
        lead={
          page.canWrite ? (
            <>
            <label className={`btn btn-default fp-btn fp-upload${uploading ? ' disabled' : ''}`} title={page.floor.has_image ? 'Replace plan' : 'Upload plan'}>
              <i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-upload'}`} aria-hidden="true"></i> <span className="fp-btn-label">{page.floor.has_image ? 'Replace plan' : 'Upload plan'}</span>
              <input
                ref={file}
                type="file"
                className="fp-file"
                accept="image/png,image/jpeg"
                hidden
                disabled={uploading}
                aria-label="Plan file (PNG or JPG)"
                onChange={(e) => upload(e.target.files?.[0])}
              />
            </label>
            {!page.floor.has_image ? (
              <button
                type="button"
                className={`btn fp-btn ${sizing ? 'btn-primary' : 'btn-default'}`}
                aria-pressed={sizing}
                data-action={canvas ? 'resize-canvas' : 'draw-canvas'}
                onClick={() => setSizing((v) => !v)}
              >
                <i className={`fas ${canvas ? 'fa-expand-arrows-alt' : 'fa-pencil-ruler'}`} aria-hidden="true"></i>{' '}
                <span className="fp-btn-label">{canvas ? 'Resize canvas' : 'Draw without a plan'}</span>
              </button>
            ) : null}
            </>
          ) : null
        }
        trail={
          page.canWrite ? (
            <div className="btn-group fp-modes" role="group" aria-label="Mode">
              <button type="button" className={`btn fp-btn ${mode === 'view' ? 'btn-primary' : 'btn-default'}`} aria-pressed={mode === 'view'} onClick={() => setMode('view')}>
                View
              </button>
              <button type="button" className={`btn fp-btn ${mode === 'edit' ? 'btn-primary' : 'btn-default'}`} aria-pressed={mode === 'edit'} onClick={() => setMode('edit')}>
                Edit
              </button>
            </div>
          ) : null
        }
      />
    </div>
  )
}

/** Marimea etajului desenat, in metri: "40 x 25 m". */
function canvasSize(page: FloorPage): string | null {
  const f = page.floor
  if (!f.scale_px_per_m || !f.image_w_px || !f.image_h_px) return null
  const m = (px: number) => String(Math.round((px / (f.scale_px_per_m as number)) * 10) / 10)
  return `${m(f.image_w_px)} x ${m(f.image_h_px)} m`
}

/**
 * "Draw without a plan" / "Resize canvas": marimea etajului in metri. Etajul
 * devine o foaie alba cu puncte, cu scara fixa - fara calibrare.
 */
function CanvasForm({ page, onDone, onCancel }: { page: FloorPage; onDone(next: FloorPage): void; onCancel(): void }) {
  const f = page.floor
  const resize = f.canvas === true && !!f.scale_px_per_m && !!f.image_w_px && !!f.image_h_px
  const side = (px: number | null, fallback: number) => (resize && px ? String(Math.round((px / (f.scale_px_per_m as number)) * 10) / 10) : String(fallback))
  const [width, setWidth] = useState(side(f.image_w_px, CANVAS_DEFAULT_M.width))
  const [length, setLength] = useState(side(f.image_h_px, CANVAS_DEFAULT_M.length))
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')
  const w = canvasSide(width)
  const l = canvasSide(length)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (w === null || l === null) {
      setProblem(`Each side must be between ${CANVAS_MIN_M} and ${CANVAS_MAX_M} m.`)
      return
    }
    setBusy(true)
    setProblem('')
    drawFloorCanvas(f.id, w, l)
      .then(onDone)
      .catch((err: unknown) => {
        setProblem(err instanceof ApiError && err.message ? err.message : 'It could not be saved.')
        setBusy(false)
      })
  }

  return (
    <form className="fp-canvas-form" onSubmit={submit} data-form={resize ? 'resize-canvas' : 'draw-canvas'}>
      <span>{resize ? 'Floor size' : 'Size of the floor'}</span>
      <input className="form-control input-sm" inputMode="decimal" aria-label="Width (m)" value={width} disabled={busy} onChange={(e) => setWidth(e.target.value)} />
      <span>x</span>
      <input className="form-control input-sm" inputMode="decimal" aria-label="Length (m)" value={length} disabled={busy} onChange={(e) => setLength(e.target.value)} />
      <span>m</span>
      <button type="submit" className="btn btn-success fp-btn" disabled={busy || w === null || l === null}>
        {resize ? 'Resize' : 'Start drawing'}
      </button>
      <button type="button" className="btn btn-default fp-btn" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
      <span className="fp-muted fp-small">
        {CANVAS_MIN_M} to {CANVAS_MAX_M} m per side.{resize ? ' Walls and items stay where they are.' : ''}
      </span>
      {problem ? (
        <span className="text-danger" role="alert">
          {problem}
        </span>
      ) : null}
    </form>
  )
}
